import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { styled } from "@mui/material/styles";

import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

import ButtonBlue from "../../../../shared/general/ButtonBlue";

import utils from "../../../../../../utils";

import { deleteEmployeeEmployeeTypeChange, getEmployeeEmployeeTypeChange, getEmployeeProfile } from "../../../../../../actions/employee";
import { companyEmployeeType } from "../../../../../../actions/company";

import DialogEdit from "./DialogEdit";
import DrawerEditEmployeeTypeChange from "./DrawerEditEmployeeTypeChange";
import Swal from "sweetalert2";

const StyledRoot = styled("div")({
  "& .salary": {
    "& .MuiTypography-h6": {
      marginRight: 8,
    },
    display: "flex",
    alignItems: "baseline",
  },
});

const StyledHeadLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
});

const StyledWrapTop = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginBottom: 16,
  "& .MuiTypography-body2": {
    margin: 0,
  },
});

const StyledDivider = styled(Divider)({
  marginTop: 16,
  marginBottom: 16,
  borderWidth: "0px 0px thin",
  borderColor: "#919eab52",
  borderStyle: "dashed",
  width: "100%",
});

const StyledTextValue = styled(Typography)({
  fontSize: 18,
  "& i": {
    padding: 2,
  },
});

const StyledWrapChange = styled("div")({
  marginTop: 8,
  "& .inner": {
    marginBottom: 8,
  },
  "& .first-line": {
    display: "flex",
    "& .date": {
      margin: "0 8px",
      fontWeight: 600,
    },
    "& .positions-name": {
      fontWeight: 600,
    },
  },
  "& .second-line": {
    marginLeft: 16,
    fontSize: 16,
  },
});

function EmployeeTypeChange(props) {
  const dispatch = useDispatch();
  const { result: employeeProfile } = useSelector(
    (state) => state.employeeProfile
  );
  const { result: employeeEmployeeType } = useSelector(
    (state) => state.employeeEmployeeType
  );
  const { t, i18n } = useTranslation();

  const [openEdit, setOpenEdit] = useState(false);
  const [showPersonalId, setShowPersonalId] = useState(false);

  const [drawerEditEmployeeTypeChangeConfig, setDrawerEditEmployeeTypeChangeConfig] = useState({
    isOpen: false,
    data: {}
  })

  useEffect(() => {
    dispatch(getEmployeeEmployeeTypeChange(employeeProfile.idEmp));
    dispatch(companyEmployeeType(employeeProfile.idCompany));
  }, []);

  const toggleDrawer = (open) => (event) => {
    console.log(open);
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenEdit(open);
  };

  const handleCloseDialog = () => {
    setOpenEdit(false);
  };

  const handleClickEditEmployeeTypeChange = (data) => {
    setDrawerEditEmployeeTypeChangeConfig(prev => ({
      ...prev,
      isOpen: true,
      data: {
        idEmployeeEmployeeType: data.idEmployeeEmployeeType,
        idEmployeeType: data.idEmployeeType,
        idEmp: data.idEmp,
        employeeId: data.employeeId,
        start: data.start,
        end: data.end,
        remark: data.remark,
      }
    }))
  }

  const handleCloseEditEmployeeTypeChange = (data) => {
    setDrawerEditEmployeeTypeChangeConfig(prev => ({
      ...prev,
      isOpen: false,
    }))
  }

  const renderDate = (value) => {
    if(!value) return t("Current");

    if(i18n.language === "th"){
      return dayjs(value).locale("th").format("DD MMM BBBB");
    } else {
      return dayjs(value).locale("en").format("DD MMM YYYY");
    }
  }

  const handleClickDeleteEmployeeEmployeeTypeChange = (data) => {
    Swal.fire({
      title: t("AreYouSure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F15E5E",
      confirmButtonText: t("Delete"),
      cancelButtonText: t("Cancel"),
      customClass: {
        container: "my-swal-container",
      }
    }).then(async (result) => {

      if (result.isConfirmed) {

        const deleteResult = await dispatch(deleteEmployeeEmployeeTypeChange(data.idEmp, data.idEmployeeEmployeeType));

        if (deleteResult && deleteResult.status === 200) {

          dispatch(getEmployeeEmployeeTypeChange(employeeProfile.idEmp));
          dispatch(getEmployeeProfile(employeeProfile.idEmp));
          
          Swal.fire({
            title: t("DataSaveSuccessful"),
            icon: "success",
            timer: 2000,
            confirmButtonColor: "#46CBE2",
            customClass: {
              container: "my-swal-container",
            }
          });
        } else if (deleteResult && deleteResult.data && deleteResult.data.message) {
          Swal.fire({
            title: t(`${deleteResult.data.message}`),
            icon: "error",
            confirmButtonColor: "#46CBE2",
            customClass: {
              container: "my-swal-container",
            }
          });
        } else {
          Swal.fire({
            title: t("AnErrorOccurred"),
            icon: "error",
            confirmButtonColor: "#46CBE2",
            customClass: {
              container: "my-swal-container",
            }
          });
        }

      }
    })
  }

  return (
    <StyledRoot>
      <StyledWrapTop>
        <div>
          <ButtonBlue
            size="small"
            startIcon={<EditIcon />}
            onClick={toggleDrawer(true)}
          >
            {t("Edit")}
          </ButtonBlue>
        </div>
      </StyledWrapTop>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Employee_Type")} ({t("Current")})
            </StyledHeadLabel>
            <Typography variant="h6">
              {employeeProfile.employeeTypeName
                ? employeeProfile.employeeTypeName
                : "-"}
            </Typography>
          </div>
        </Grid>
      </Grid>
      <StyledDivider />
      <div>
        <StyledHeadLabel color="text.secondary">
          {t("ChangeLog")}
        </StyledHeadLabel>
        {employeeEmployeeType && (
          <StyledWrapChange>
            {[...employeeEmployeeType].reverse().map((item, index) => (
              <Box display="flex" key={index} marginBottom="8px">
                <Box display="flex" alignItems="flex-start" marginRight="8px">
                  <IconButton
                    aria-label="delete"
                    size="small"
                    onClick={() => {
                      handleClickDeleteEmployeeEmployeeTypeChange(item)
                    }}
                    disabled={index > 0 || employeeEmployeeType.length === 1}
                  >
                    <HighlightOffRoundedIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    onClick={() => {
                      handleClickEditEmployeeTypeChange(item)
                    }}
                    disabled={index > 0}
                  >
                    <EditNoteRoundedIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                <Box flexGrow="1">
                  <Box display="flex">
                    <Typography color="text.third" fontWeight="600" marginRight="8px">
                      {`[${renderDate(item.start)} - ${renderDate(item.end)}]`}
                    </Typography>
                    <Typography fontWeight="600">
                      {item.employeeTypeName}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" fontWeight="500">
                    {`${t("EmployeeID")}: ${item.employeeId? item.employeeId: "-"}`}
                  </Typography>
                  <Typography color="text.secondary" fontWeight="500">
                    {`${t("Reason")}: ${item.remark? item.remark: "-"}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </StyledWrapChange>
        )}
      </div>
      <DialogEdit
        open={openEdit}
        toggleDrawer={toggleDrawer}
        handleCloseDialog={handleCloseDialog}
      />
      <DrawerEditEmployeeTypeChange
        drawerConfig={drawerEditEmployeeTypeChangeConfig}
        handleClose={handleCloseEditEmployeeTypeChange}
      />
    </StyledRoot>
  );
}

export default EmployeeTypeChange;
